/**
 * device/apps/camera.ts — Camera & Photo Gallery
 *
 * A camera app for the hPhone/hPad. Two tabs:
 *
 *   Camera  — viewfinder with time-of-day tinted gradient, corner brackets,
 *             room name overlay, shutter button. Tap the shutter to capture
 *             a snapshot entry into the gallery.
 *
 *   Gallery — tight grid of saved photo thumbnails. Each thumbnail is a
 *             colored rectangle encoding the time/season/room of the shot.
 *             Tap a thumbnail to highlight it.
 *
 * All rendering is Canvas 2D — zero DOM.
 */

import type { GameState } from "../../engine/state.js";
import type { DeviceApp, Rect } from "../app.js";
import { registerApp, drawRoundRect, drawText } from "../app.js";

// ─── Types ───────────────────────────────────────────────────────────────

interface PhotoEntry {
  id: number;
  roomName: string;
  gameHour: number;
  season: string;
  timestamp: number; // real time ms
}

interface CameraAppState {
  tab: "camera" | "gallery";
  photos: PhotoEntry[];
  selectedPhotoId: number | null;
  flashAnim: number;    // 0-1, decays each frame when a photo is taken
  nextId: number;
}

// ─── Time-of-day Palette ─────────────────────────────────────────────────

/** Return a background gradient pair based on the current game hour */
function timeTint(hour: number): [string, string] {
  // dawn: 5-7
  if (hour >= 5 && hour < 7) return ["#f9a8d4", "#fbbf24"]; // warm pink to gold
  // morning: 7-11
  if (hour >= 7 && hour < 11) return ["#93c5fd", "#bfdbfe"]; // light blue
  // midday: 11-15
  if (hour >= 11 && hour < 15) return ["#60a5fa", "#38bdf8"]; // bright sky
  // afternoon: 15-18
  if (hour >= 15 && hour < 18) return ["#fbbf24", "#fb923c"]; // warm amber
  // dusk: 18-20
  if (hour >= 18 && hour < 20) return ["#f97316", "#9333ea"]; // amber to purple
  // evening: 20-22
  if (hour >= 20 && hour < 22) return ["#6366f1", "#1e1b4b"]; // deep indigo
  // night: 22-5
  return ["#1e1b4b", "#0f172a"]; // deep blue to near-black
}

/** Season accent color for subtle overlay tinting */
function seasonAccent(season: string): string {
  switch (season) {
    case "spring": return "rgba(134,239,172,0.12)";
    case "summer": return "rgba(253,224,71,0.10)";
    case "autumn": return "rgba(251,146,60,0.14)";
    case "winter": return "rgba(186,230,253,0.12)";
    default: return "rgba(255,255,255,0)";
  }
}

/** Format a game hour as HH:MM string */
function formatTime(hour: number): string {
  const h = Math.floor(hour) % 24;
  const m = Math.floor((hour % 1) * 60);
  const hh = h < 10 ? "0" + h : "" + h;
  const mm = m < 10 ? "0" + m : "" + m;
  return hh + ":" + mm;
}

// ─── Default State ───────────────────────────────────────────────────────

function defaultState(): CameraAppState {
  return {
    tab: "camera",
    photos: [
      { id: 1, roomName: "Lobby", gameHour: 14.5, season: "summer", timestamp: Date.now() - 86400000 },
      { id: 2, roomName: "Rooftop Garden", gameHour: 6.25, season: "spring", timestamp: Date.now() - 43200000 },
      { id: 3, roomName: "Cozy Den", gameHour: 21.0, season: "autumn", timestamp: Date.now() - 7200000 },
    ],
    selectedPhotoId: null,
    flashAnim: 0,
    nextId: 4,
  };
}

/** Retrieve (or initialize) persisted camera state */
function getAppState(state: GameState): CameraAppState {
  let s = state.player.device.appStates["camera"] as CameraAppState | undefined;
  if (!s) {
    s = defaultState();
    state.player.device.appStates["camera"] = s;
  }
  return s;
}

// ─── Drawing Helpers ─────────────────────────────────────────────────────

/** Draw corner bracket marks at the four corners of a rectangle */
function drawBrackets(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  len: number, thickness: number, color: string,
): void {
  ctx.strokeStyle = color;
  ctx.lineWidth = thickness;
  ctx.lineCap = "round";

  // top-left
  ctx.beginPath();
  ctx.moveTo(x, y + len);
  ctx.lineTo(x, y);
  ctx.lineTo(x + len, y);
  ctx.stroke();

  // top-right
  ctx.beginPath();
  ctx.moveTo(x + w - len, y);
  ctx.lineTo(x + w, y);
  ctx.lineTo(x + w, y + len);
  ctx.stroke();

  // bottom-right
  ctx.beginPath();
  ctx.moveTo(x + w, y + h - len);
  ctx.lineTo(x + w, y + h);
  ctx.lineTo(x + w - len, y + h);
  ctx.stroke();

  // bottom-left
  ctx.beginPath();
  ctx.moveTo(x + len, y + h);
  ctx.lineTo(x, y + h);
  ctx.lineTo(x, y + h - len);
  ctx.stroke();
}

/** Draw a photo thumbnail (colored rectangle with room/time overlay) */
function drawThumbnail(
  ctx: CanvasRenderingContext2D,
  photo: PhotoEntry,
  tx: number, ty: number, tw: number, th: number,
  selected: boolean,
): void {
  // Background gradient from the photo's time of day
  const [c1, c2] = timeTint(photo.gameHour);
  const grad = ctx.createLinearGradient(tx, ty, tx, ty + th);
  grad.addColorStop(0, c1);
  grad.addColorStop(1, c2);
  ctx.fillStyle = grad;
  drawRoundRect(ctx, tx, ty, tw, th, 3);
  ctx.fill();

  // Season tint overlay
  ctx.fillStyle = seasonAccent(photo.season);
  drawRoundRect(ctx, tx, ty, tw, th, 3);
  ctx.fill();

  // Clip text to thumbnail bounds
  ctx.save();
  ctx.beginPath();
  ctx.rect(tx, ty, tw, th);
  ctx.clip();

  // Time stamp (top-left)
  drawText(ctx, formatTime(photo.gameHour), tx + 2, ty + 2, tw - 4, "5px sans-serif", "rgba(255,255,255,0.7)", "left");
  // Room name (bottom)
  drawText(ctx, photo.roomName, tx + 2, ty + th - 11, tw - 4, "bold 5px sans-serif", "rgba(255,255,255,0.9)", "left");

  ctx.restore();

  // Selection highlight border
  if (selected) {
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 2;
    drawRoundRect(ctx, tx, ty, tw, th, 3);
    ctx.stroke();
  }
}

// ─── Cached Layout ───────────────────────────────────────────────────────
// Computed each frame during draw, consumed by handleTap to avoid
// recomputing hit regions.

interface LayoutCache {
  tabCameraRect: Rect;
  tabGalleryRect: Rect;
  shutterCx: number;
  shutterCy: number;
  shutterR: number;
  galleryGrid: Array<{ photo: PhotoEntry; x: number; y: number; w: number; h: number }>;
}

let _layout: LayoutCache | null = null;

// ─── View Renderers ──────────────────────────────────────────────────────

/** Draw the camera viewfinder view */
function drawCameraView(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  state: GameState, cs: CameraAppState,
): void {
  const hour = state.time.gameHour;
  const season = state.time.season;
  const roomName = state.currentRoom?.name ?? "Outside";

  // ── Viewfinder area ────────────────────────────────────────
  const vfPad = 8;
  const shutterAreaH = 50;
  const vfX = x + vfPad;
  const vfY = y + 6;
  const vfW = w - vfPad * 2;
  const vfH = h - shutterAreaH - 12;

  // Viewfinder background: time-of-day gradient
  const [c1, c2] = timeTint(hour);
  const grad = ctx.createLinearGradient(vfX, vfY, vfX, vfY + vfH);
  grad.addColorStop(0, c1);
  grad.addColorStop(1, c2);
  ctx.fillStyle = grad;
  drawRoundRect(ctx, vfX, vfY, vfW, vfH, 6);
  ctx.fill();

  // Season overlay tint
  ctx.fillStyle = seasonAccent(season);
  drawRoundRect(ctx, vfX, vfY, vfW, vfH, 6);
  ctx.fill();

  // Subtle diagonal hatching for atmosphere
  ctx.save();
  ctx.beginPath();
  ctx.rect(vfX, vfY, vfW, vfH);
  ctx.clip();
  ctx.strokeStyle = "rgba(255,255,255,0.04)";
  ctx.lineWidth = 0.5;
  for (let i = -vfH; i < vfW; i += 8) {
    ctx.beginPath();
    ctx.moveTo(vfX + i, vfY);
    ctx.lineTo(vfX + i + vfH, vfY + vfH);
    ctx.stroke();
  }
  ctx.restore();

  // Stylized horizon line
  const horizonY = vfY + vfH * 0.6;
  ctx.fillStyle = "rgba(0,0,0,0.08)";
  ctx.fillRect(vfX, horizonY, vfW, vfY + vfH - horizonY);

  // Corner brackets
  const bracketLen = Math.min(vfW, vfH) * 0.12;
  drawBrackets(
    ctx, vfX + 6, vfY + 6, vfW - 12, vfH - 12,
    bracketLen, 1.5, "rgba(255,255,255,0.7)",
  );

  // Center crosshair (small +)
  const ccx = vfX + vfW / 2;
  const ccy = vfY + vfH / 2;
  const crossLen = 4;
  ctx.strokeStyle = "rgba(255,255,255,0.35)";
  ctx.lineWidth = 0.5;
  ctx.beginPath();
  ctx.moveTo(ccx - crossLen, ccy);
  ctx.lineTo(ccx + crossLen, ccy);
  ctx.moveTo(ccx, ccy - crossLen);
  ctx.lineTo(ccx, ccy + crossLen);
  ctx.stroke();

  // ── HUD Overlays ───────────────────────────────────────────
  // Top-left: room name
  drawText(
    ctx, roomName, vfX + 10, vfY + 10, vfW - 50,
    "bold 7px sans-serif", "rgba(255,255,255,0.85)", "left",
  );
  // Top-right: time
  drawText(
    ctx, formatTime(hour), vfX + vfW - 10, vfY + 10, 40,
    "7px monospace", "rgba(255,255,255,0.7)", "right",
  );
  // Bottom-left: season badge
  const seasonLabel = season.charAt(0).toUpperCase() + season.slice(1);
  drawText(
    ctx, seasonLabel, vfX + 10, vfY + vfH - 16, vfW * 0.4,
    "6px sans-serif", "rgba(255,255,255,0.5)", "left",
  );

  // ── Flash indicator (lightning bolt icon, top-right) ───────
  const flX = vfX + vfW - 22;
  const flY = vfY + 24;
  ctx.fillStyle = cs.flashAnim > 0
    ? "rgba(255,255,255,0.9)"
    : "rgba(255,255,255,0.4)";
  ctx.beginPath();
  ctx.moveTo(flX + 4, flY);
  ctx.lineTo(flX, flY + 6);
  ctx.lineTo(flX + 3, flY + 6);
  ctx.lineTo(flX + 1, flY + 11);
  ctx.lineTo(flX + 6, flY + 4);
  ctx.lineTo(flX + 3, flY + 4);
  ctx.lineTo(flX + 4, flY);
  ctx.fill();

  // ── Shutter Button ─────────────────────────────────────────
  const sCx = x + w / 2;
  const sCy = y + h - shutterAreaH / 2;
  const sR = 16;

  // Outer ring
  ctx.strokeStyle = "rgba(255,255,255,0.9)";
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  ctx.arc(sCx, sCy, sR, 0, Math.PI * 2);
  ctx.stroke();

  // Inner fill
  ctx.fillStyle = "#ffffff";
  ctx.beginPath();
  ctx.arc(sCx, sCy, sR - 4, 0, Math.PI * 2);
  ctx.fill();

  // Photo count badge to the right of shutter
  if (cs.photos.length > 0) {
    const bx = sCx + sR + 14;
    ctx.fillStyle = "rgba(255,255,255,0.15)";
    ctx.beginPath();
    ctx.arc(bx, sCy, 8, 0, Math.PI * 2);
    ctx.fill();
    drawText(
      ctx, "" + cs.photos.length, bx, sCy - 4, 14,
      "bold 6px sans-serif", "rgba(255,255,255,0.7)", "center",
    );
  }

  // Store shutter location for tap detection
  if (_layout) {
    _layout.shutterCx = sCx;
    _layout.shutterCy = sCy;
    _layout.shutterR = sR;
  }

  // ── Flash overlay (white wash over viewfinder) ─────────────
  if (cs.flashAnim > 0) {
    ctx.fillStyle = "rgba(255,255,255," + (cs.flashAnim * 0.8) + ")";
    drawRoundRect(ctx, vfX, vfY, vfW, vfH, 6);
    ctx.fill();
  }
}

/** Draw the gallery grid view */
function drawGalleryView(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  cs: CameraAppState,
): void {
  const pad = 6;
  const gap = 4;
  const cols = 3;
  const thumbW = (w - pad * 2 - gap * (cols - 1)) / cols;
  const thumbH = thumbW * 0.75; // 4:3 ratio

  if (cs.photos.length === 0) {
    drawText(
      ctx, "No photos yet", x + w / 2, y + h / 2 - 10, w - 20,
      "bold 9px sans-serif", "rgba(255,255,255,0.4)", "center",
    );
    drawText(
      ctx, "Take your first shot!", x + w / 2, y + h / 2 + 4, w - 20,
      "7px sans-serif", "rgba(255,255,255,0.25)", "center",
    );
    return;
  }

  const grid: LayoutCache["galleryGrid"] = [];

  for (let i = 0; i < cs.photos.length; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const tx = x + pad + col * (thumbW + gap);
    const ty = y + pad + row * (thumbH + gap);

    // Don't draw beyond the visible area
    if (ty + thumbH > y + h) break;

    const photo = cs.photos[i];
    const isSelected = cs.selectedPhotoId === photo.id;
    drawThumbnail(ctx, photo, tx, ty, thumbW, thumbH, isSelected);

    grid.push({ photo, x: tx, y: ty, w: thumbW, h: thumbH });
  }

  if (_layout) {
    _layout.galleryGrid = grid;
  }
}

// ─── Hit Testing ─────────────────────────────────────────────────────────

function hitRect(px: number, py: number, r: Rect): boolean {
  return px >= r.x && px <= r.x + r.w && py >= r.y && py <= r.y + r.h;
}

function hitCircle(px: number, py: number, cx: number, cy: number, r: number): boolean {
  const dx = px - cx;
  const dy = py - cy;
  return dx * dx + dy * dy <= r * r;
}

// ─── Camera App ──────────────────────────────────────────────────────────

const cameraApp: DeviceApp = {
  id: "camera",
  name: "Camera",
  iconGradient: ["#f59e0b", "#f97316"],

  // ── Icon: camera lens (outer ring + inner circle + glint) ──────────
  drawIcon(ctx, cx, cy, size) {
    const outer = size * 0.32;
    const inner = size * 0.18;
    const glint = size * 0.07;

    // Outer ring
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(cx, cy, outer, 0, Math.PI * 2);
    ctx.stroke();

    // Inner filled circle
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.arc(cx, cy, inner, 0, Math.PI * 2);
    ctx.fill();

    // Glint highlight
    ctx.fillStyle = "rgba(255,255,255,0.5)";
    ctx.beginPath();
    ctx.arc(cx - outer * 0.35, cy - outer * 0.35, glint, 0, Math.PI * 2);
    ctx.fill();
  },

  // ── Content ────────────────────────────────────────────────────────
  drawContent(ctx, rect, state, dt) {
    const { x, y, w, h } = rect;
    const cs = getAppState(state);

    // Decay flash animation
    if (cs.flashAnim > 0) {
      cs.flashAnim = Math.max(0, cs.flashAnim - dt * 4);
    }

    // ── Tab Bar ──────────────────────────────────────────────
    const tabH = 22;
    const tabY = y;
    const halfW = w / 2;

    const camTab: Rect = { x, y: tabY, w: halfW, h: tabH };
    const galTab: Rect = { x: x + halfW, y: tabY, w: halfW, h: tabH };

    // Tab backgrounds
    drawRoundRect(ctx, camTab.x, camTab.y, camTab.w, camTab.h, 0,
      cs.tab === "camera" ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.04)");
    drawRoundRect(ctx, galTab.x, galTab.y, galTab.w, galTab.h, 0,
      cs.tab === "gallery" ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.04)");

    // Tab labels
    drawText(ctx, "Camera", x + halfW / 2, tabY + 6, halfW - 8,
      "bold 8px sans-serif",
      cs.tab === "camera" ? "#ffffff" : "rgba(255,255,255,0.5)", "center");
    drawText(ctx, "Gallery (" + cs.photos.length + ")", x + halfW + halfW / 2, tabY + 6, halfW - 8,
      "bold 8px sans-serif",
      cs.tab === "gallery" ? "#ffffff" : "rgba(255,255,255,0.5)", "center");

    // Subtle separator line
    ctx.fillStyle = "rgba(255,255,255,0.08)";
    ctx.fillRect(x, tabY + tabH - 1, w, 1);

    // Content area below the tabs
    const contentY = tabY + tabH;
    const contentH = h - tabH;

    // Cache layout for tap handling
    _layout = {
      tabCameraRect: camTab,
      tabGalleryRect: galTab,
      shutterCx: 0,
      shutterCy: 0,
      shutterR: 0,
      galleryGrid: [],
    };

    if (cs.tab === "camera") {
      drawCameraView(ctx, x, contentY, w, contentH, state, cs);
    } else {
      drawGalleryView(ctx, x, contentY, w, contentH, cs);
    }
  },

  // ── Tap Handling ───────────────────────────────────────────────────
  handleTap(localX, localY, state) {
    if (!_layout) return false;

    const cs = getAppState(state);

    // Tab: Camera
    if (hitRect(localX, localY, _layout.tabCameraRect)) {
      cs.tab = "camera";
      cs.selectedPhotoId = null;
      return true;
    }

    // Tab: Gallery
    if (hitRect(localX, localY, _layout.tabGalleryRect)) {
      cs.tab = "gallery";
      return true;
    }

    if (cs.tab === "camera") {
      // Shutter button (circle hit test with a few px forgiveness)
      if (hitCircle(localX, localY, _layout.shutterCx, _layout.shutterCy, _layout.shutterR + 4)) {
        const photo: PhotoEntry = {
          id: cs.nextId++,
          roomName: state.currentRoom?.name ?? "Outside",
          gameHour: state.time.gameHour,
          season: state.time.season,
          timestamp: Date.now(),
        };
        cs.photos.unshift(photo); // newest first
        cs.flashAnim = 1;         // trigger flash
        return true;
      }
    }

    if (cs.tab === "gallery") {
      // Thumbnail tap — toggle selection
      for (let i = 0; i < _layout.galleryGrid.length; i++) {
        const cell = _layout.galleryGrid[i];
        if (localX >= cell.x && localX <= cell.x + cell.w &&
            localY >= cell.y && localY <= cell.y + cell.h) {
          cs.selectedPhotoId = cs.selectedPhotoId === cell.photo.id ? null : cell.photo.id;
          return true;
        }
      }
      // Tap empty area — deselect
      cs.selectedPhotoId = null;
    }

    return false;
  },

  onOpen(state) {
    getAppState(state);
  },

  badge(_state) {
    return 0;
  },
};

// ─── Register ────────────────────────────────────────────────────────────

registerApp(cameraApp);

export { cameraApp };
